import { useState } from "react";
import { ACCOUNTING_TARGET_MEASURES } from "@dentacore/shared-types";
import type { AccountingTargetKey, AccountingTargets } from "@dentacore/shared-types";

import { Modal } from "../ui/modal";
import { basisPointsText, parseBasisPoints } from "./format";
import styles from "./accounting.module.css";

/** What each measure is called, and what it is judged against, in a person's words. */
const MEASURES: Record<AccountingTargetKey, { label: string; hint: string }> = {
  collectionRate: {
    label: "Collection rate",
    hint: "At least — collected ÷ billed in the period.",
  },
  overhead: {
    label: "Overhead",
    hint: "At most — money out ÷ money in in the period.",
  },
  owedOver60: {
    label: "Owed over 60 days",
    hint: "At most — the share of what is owed that is more than 60 days old.",
  },
};

/**
 * *Targets* — the clinic's own goals for three figures (STORY-182c, AF-D6).
 *
 * **Blank means no target**, and nothing is filled in for anyone: the benchmarks
 * published for dental practices are rules of thumb that differ by source,
 * specialty and payer mix, so the figure that turns green or warm is one this
 * clinic chose. A tile is coloured against a target only when there is one.
 *
 * Typed as a percent, between 0.01 and 100 with at most two decimals, and sent
 * as basis points. All three are saved together, so a failure leaves none
 * half-changed.
 */
export function TargetsDialog({
  targets,
  busy,
  onSave,
  onClose,
}: {
  targets: AccountingTargets;
  busy: boolean;
  /** Resolves true once saved. A failure leaves the dialog open with what was typed. */
  onSave: (targets: AccountingTargets) => Promise<boolean>;
  onClose: () => void;
}): React.JSX.Element {
  const [typed, setTyped] = useState<Record<AccountingTargetKey, string>>({
    collectionRate: basisPointsText(targets.collectionRate),
    overhead: basisPointsText(targets.overhead),
    owedOver60: basisPointsText(targets.owedOver60),
  });
  const [error, setError] = useState<string | null>(null);

  const save = (): void => {
    const next: Partial<AccountingTargets> = {};
    for (const { key } of ACCOUNTING_TARGET_MEASURES) {
      const parsed = parseBasisPoints(typed[key]);
      if (parsed === "invalid") {
        setError(
          `${MEASURES[key].label} must be a percent between 0.01 and 100, with at most two decimals — or left blank for no target.`,
        );
        return;
      }
      next[key] = parsed;
    }
    setError(null);
    void onSave(next as AccountingTargets).then((landed) => {
      if (landed) onClose();
    });
  };

  return (
    <Modal label="Accounting targets" onClose={onClose} testId="targets-dialog">
      <form
        className={styles.dialogForm}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <h2 className={styles.dialogTitle}>Targets</h2>
        <p className={styles.dialogSub}>
          Your clinic&rsquo;s own goals. A figure turns green or warm against a target only when you
          set one; leave a box blank for none.
        </p>

        <div className={styles.dialogGrid}>
          {ACCOUNTING_TARGET_MEASURES.map(({ key }) => (
            <label key={key} className={styles.field}>
              <span className={styles.fieldLabel}>{MEASURES[key].label} (%)</span>
              <input
                className={styles.input}
                inputMode="decimal"
                value={typed[key]}
                placeholder="No target"
                onChange={(event) => {
                  setError(null);
                  setTyped((was) => ({ ...was, [key]: event.target.value }));
                }}
                data-testid={`target-${key}`}
              />
              <span className={styles.note}>{MEASURES[key].hint}</span>
            </label>
          ))}
        </div>

        {error !== null && <p className={styles.error}>{error}</p>}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy}
            data-testid="targets-save"
          >
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}
